import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Search, Eye, MoreVertical, CheckCircle, FileEdit, AlertCircle, RefreshCw } from "lucide-react";
import API_CONSTRUCTOR from "../../../models/api-model";
import { getStatusColor, formatStatus, formatDate } from "../../../utils/adminUtils";
import { getPropertyField } from "../../../utils/propertyDataHelper";

/**
 * Admin Queries Page
 * Lists all queries with filters and actions
 */
export const AdminQueries = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  
  // Initialize API
  const api = API_CONSTRUCTOR();

  useEffect(() => {
    loadQueries();
  }, [statusFilter]);

  const loadQueries = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = "/queries?";
      if (statusFilter && statusFilter !== "all") {
        url += `status=${statusFilter}&`;
      }
      url += "sortBy=created_at&sortOrder=DESC";

      const response = await api.get(url);
      setQueries(response.data || []);
    } catch (err) {
      console.error("Error loading queries:", err);
      setError(err.response?.data?.error || "Failed to load queries");
    } finally {
      setLoading(false);
    }
  };

  // Filter queries by search term
  const filteredQueries = queries.filter((query) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const address = query.property_address || getPropertyField(query, 'address', '');
    return (
      address.toLowerCase().includes(searchLower) ||
      query.homeowner_name?.toLowerCase().includes(searchLower) ||
      query.homeowner_email?.toLowerCase().includes(searchLower)
    );
  });

  if (error && !loading) {
    return (
      <div className="space-y-6">
        {/* Page Header with Refresh */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Query Management</h2>
            <p className="text-muted-foreground">Manage all flip requests from homeowners</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadQueries}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Query Management</h2>
          <p className="text-muted-foreground">Manage all flip requests from homeowners</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={loadQueries}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by address, homeowner name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_inspection_payment">Pending Payment</SelectItem>
                <SelectItem value="pending_agent">Pending Agent</SelectItem>
                <SelectItem value="pending_owner_approval">Pending Owner</SelectItem>
                <SelectItem value="pending_investor">Pending Investor</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Queries Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            All Queries {filteredQueries.length > 0 && `(${filteredQueries.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading queries...</div>
          ) : filteredQueries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || statusFilter !== "all"
                ? "No queries match your filters"
                : "No queries found"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property Address</TableHead>
                    <TableHead>Homeowner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Inspection</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQueries.map((query) => (
                    <TableRow 
                      key={query.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/admin/queries/${query.id}`)}
                    >
                      <TableCell className="font-medium">
                        {query.property_address || getPropertyField(query, 'address', 'N/A')}
                        <div className="text-xs text-muted-foreground">
                          {query.property_city || getPropertyField(query, 'city')}, {query.property_state || getPropertyField(query, 'state')}
                        </div>
                      </TableCell>
                      <TableCell>
                        {query.homeowner_name}
                        <div className="text-xs text-muted-foreground">{query.homeowner_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(query.status)} variant="secondary">
                          {formatStatus(query.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {query.inspection_fee_paid ? (
                          <Badge className="bg-green-100 text-green-800" variant="secondary">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800" variant="secondary">
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(query.created_at)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate(`/admin/queries/${query.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {!query.inspection_fee_paid && (
                              <DropdownMenuItem onClick={() => navigate(`/admin/queries/${query.id}`)}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm Payment
                              </DropdownMenuItem>
                            )}
                            {query.status === "pending_agent" && (
                              <DropdownMenuItem onClick={() => navigate(`/admin/calculations/new/${query.id}`)}>
                                <FileEdit className="w-4 h-4 mr-2" />
                                Create Calculation
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminQueries;

